import React, { useState, useEffect } from "react";
import { Table, DatePicker, Select, Button, Space, message, Flex } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { get, post } from '@/common/services/httpClient';
import { API_ENDPOINTS, QUERY_KEYS } from '@/features/auditLog/api/apiEndpoints';
import type { AuditConfigResponse, AuditLogResponse, AuditLogType, DistinctEntitiesResponse, SearchAuditLogRequest } from "../types/auditLogType";
import Export from "../../../common/components/Export";
import { entityFieldMappings } from "@/common/staticData/auditFieldMappings";
import StatusTag from "../../../common/components/Status";

const { RangePicker } = DatePicker;

const AuditLogPage: React.FC = () => {
    const { t } = useTranslation();

    //  Filters state
    const [filters, setFilters] = useState({
        entityName: undefined as string | undefined,
        fieldName: null,
        dateRange: undefined as [dayjs.Dayjs, dayjs.Dayjs] | undefined,
    });

    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [apiError, setApiError] = useState<string | null>(null);


    // Fetch config (max date range days)
    const { data: configData, isLoading: isConfigLoading } = useQuery<AuditConfigResponse>({
        queryKey: [QUERY_KEYS.AUDIT_LOG.CONFIG],
        queryFn: () => get(API_ENDPOINTS.AUDIT_LOG.CONFIG),
    });

    // Fetch Distinct Entities
    const { data: distinctEntitiesResponse } = useQuery<DistinctEntitiesResponse>({
        queryKey: [QUERY_KEYS.AUDIT_LOG.DISTINCT_ENTITIES],
        queryFn: () => get(API_ENDPOINTS.AUDIT_LOG.DISTINCT_ENTITIES),
    });

    const getAuditLogs = async (payload: SearchAuditLogRequest): Promise<AuditLogResponse> => {
        const response = await post<AuditLogResponse>(API_ENDPOINTS.AUDIT_LOG.SEARCH, payload);

        if (!response || response.isError) {
            throw new Error(response?.message || "Failed to fetch audit logs");
        }
        return response;
    };

    const { data, isLoading, refetch } = useQuery<AuditLogResponse>({
        queryKey: [QUERY_KEYS.AUDIT_LOG.SEARCH, filters, pageNumber, pageSize],
        queryFn: () =>
            getAuditLogs({
                ...filters,
                fromDate: filters.dateRange?.[0]
                    ? filters.dateRange[0].startOf("day").format("YYYY-MM-DDTHH:mm:ss")
                    : undefined,
                toDate: filters.dateRange?.[1]
                    ? filters.dateRange[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss")
                    : undefined,
                pageNumber,
                pageSize,
            }),
        enabled: false,
    });

    useEffect(() => {
        if (!configData?.maxDateRangeDays) return;

        const maxDays = configData.maxDateRangeDays;
        const end = dayjs();
        const start = dayjs().subtract(maxDays - 1, "days");

        setFilters({
            entityName: undefined,
            fieldName: null, 
            dateRange: [start, end],
        });

        // Step 2: Wait one render tick for filters to update, then refetch
        const timer = setTimeout(() => {
            refetch();
        }, 100);

        return () => clearTimeout(timer);

    }, [configData]); 


    useEffect(() => {
        //If query hasn't returned anything yet, skip
        if (!data) return;

        if (data.isError) {
            message.error(data.message || "Failed to load data");
        }
    }, [data]);


    //handle loading after all hooks
    if (isConfigLoading) {
        return <div>Loading configuration...</div>;
    }

    if (!distinctEntitiesResponse) {
        return <div>Loading entities...</div>;
    }

    // Use API value or fallback
    const maxDays = configData?.maxDateRangeDays ?? 30;

    const handleSearch = async () => {
        setApiError(null); // Clear previous error

        if (!filters.dateRange || !filters.dateRange[0] || !filters.dateRange[1]) {
            message.warning("Please select a valid date range.");
            return;
        }

        const diff = filters.dateRange[1].diff(filters.dateRange[0], "days");

        if ((diff - 1) > maxDays) {
            message.warning(`Date range cannot exceed ${maxDays} days.`);
            return;
        }

        try {
            const result = await refetch();
            const response = result?.data;

            if (response?.isError === true) {
                setApiError(response.message || "Something went wrong");
                message.error(response.message || "Something went wrong");
                return;
            }

        } catch (err: any) {
            message.error(err?.response?.data?.message || "Something went wrong");
        }

    };

    const handleReset = async () => {
        const maxDays = configData?.maxDateRangeDays ?? 7; // fallback to 7 if API not loaded

        const newFilters = {
            entityName: undefined,
            fieldName: null,
            dateRange: [dayjs().subtract(maxDays - 1, "days"), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs], // ✅ tuple assertion
        };
        setFilters(newFilters);
        setPageNumber(1);

        try {
            // Wait one tick so filters update before calling refetch
            await new Promise((r) => setTimeout(r, 50));

            const result = await refetch();
            if (result.data) {
                const response = result.data;

                if (response.isError) {
                    message.error(response.message || "Something went wrong");
                }
            } else if (result.error) {
                console.error(" Error from refetch:", result.error);
                message.error(result.error.message || "Something went wrong");
            } else {
                console.warn("No data returned, result:", result);
            }
        } catch (err: any) {
            message.error(err?.response?.data?.message || "Something went wrong");
        }
    };

    // Table Columns
    const columns: ColumnsType<AuditLogType> = [
        { title: t("Entity Name"), dataIndex: "entityName", width: 180, fixed: 'left' },
        //{ title: "Entity ID", dataIndex: "entityId" },
        { title: t("Field Name"), dataIndex: "fieldName", width: 200 },
        {
            title: t("Old Value"), dataIndex: "oldValue", width: 250,
            render: (text) => (
                <div
                    style={{
                        maxWidth: 250,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowY: "auto",
                        maxHeight: "80px",
                    }}
                >
                    {text}
                </div>
            ),
        },
        {
            title: t("New Value"), dataIndex: "newValue", width: 250,
            render: (text) => (
                <div
                    style={{
                        maxWidth: 250,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowY: "auto",
                        maxHeight: "80px",
                    }}
                >
                    {text}
                </div>
            ),
        },
        { title: t("Username"), dataIndex: "userName" },
        //{ title: "Changed By", dataIndex: "changedBy" },
        {
            title: t("Changed On"),
            dataIndex: "changedOn",
            render: (val) => dayjs(val).format("DD-MM-YYYY HH:mm"),
        },
        { title: t("Changed IP"), dataIndex: "changedIp" },
        {
            title: t("Action Type"), dataIndex: "actionType", width: 120, fixed: 'right', align: 'center',
            render: (status: string) => (<StatusTag status={status} />)
        },
    ];


    // Map options for Select
    const distinctEntityOptions = Array.isArray(distinctEntitiesResponse?.data) && distinctEntitiesResponse.data.length > 0
        ? distinctEntitiesResponse.data.map((name) => ({
            label: name,
            value: name,
        }))
        : [{ label: "No Entities Found", value: "" }];


    return (
        <div className="p-4 bg-white rounded-lg shadow-md">
            {apiError && (
                <div style={{ color: "red" }}>Error: {apiError}</div>
            )}

            {/* Title + Export Button (Header Section) */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                }}
            >
                {/* Header */}
                {/*<h1 className="text-xl font-semibold">{t("Audit Logs")}</h1>*/}
                <h1>{t("Audit Logs")}</h1>
                <Flex gap="small" wrap>
                    <Export
                        compact={true}
                        apiUrl={API_ENDPOINTS.AUDIT_LOG.EXPORT}
                        fileName="AuditLogs"
                        fileType="excel"
                        confirmButtonText="Do you want to export Audit Logs?"
                        columns={[
                            { header: "Entity Name", key: "entityName" },
                            { header: "Field Name", key: "fieldName" },
                            { header: "Old Value", key: "oldValue" },
                            { header: "New Value", key: "newValue" },
                            { header: "Username", key: "userName" },
                            { header: "Changed On", key: "changedOn", transform: (val) => dayjs(val).format("DD-MM-YYYY HH:mm:ss") },
                            { header: "Changed IP", key: "changedIp" },
                            { header: "Action Type", key: "actionType" },
                        ]}
                        defaultPayload={{ IsExport: true }}
                        extraPayload={{
                            entityName: filters?.entityName,
                            fromDate: filters?.dateRange?.[0]?.format("YYYY-MM-DD"),
                            toDate: filters?.dateRange?.[1]?.format("YYYY-MM-DD"),
                        }}
                    />
                </Flex>
            </div>

            {/* Filters */}
            <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
                <Select
                    placeholder={t("Select Entity")}
                    style={{ width: 200 }}
                    allowClear
                    size="large"
                    value={filters.entityName}
                    onChange={(value) =>
                        setFilters({
                            ...filters,
                            entityName: value,
                            fieldName: null,
                        })
                    }
                    options={distinctEntityOptions}
                />

                <Select
                    placeholder={t("Select Field")}
                    style={{ width: 200 }}
                    allowClear
                    size="large"
                    disabled={!filters.entityName} // disable until entity selected
                    value={filters.fieldName}
                    onChange={(value) => setFilters({ ...filters, fieldName: value })}
                    options={filters.entityName ? entityFieldMappings[filters.entityName] || [] : []}
                />

                <RangePicker
                    size="large"
                    value={filters.dateRange}
                    onChange={(range) => setFilters({ ...filters, dateRange: range as [dayjs.Dayjs, dayjs.Dayjs] })}
                />

                <Button size="large" type="primary" icon={<SearchOutlined />} onClick={handleSearch} />
                <Button size="large" icon={<ReloadOutlined />} onClick={handleReset} />
            </Space>

            {/* Error Message */}
            {apiError && (
                <div className="mb-2 text-red-600 font-medium">
                    {apiError}
                </div>
            )}

            {/* Table */}
            <Table
                columns={columns}
                dataSource={apiError ? [] : data?.data || []}
                loading={isLoading}
                pagination={{
                    total: data?.paging?.totalCount || 0,
                    current: pageNumber,
                    pageSize,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        t("table.pagination.showTotal", {
                            start: range[0],
                            end: range[1],
                            total,
                            defaultValue: `${range[0]}-${range[1]} of ${total} items`,
                        }),
                    onChange: async (page, size) => {
                        setPageNumber(page);
                        setPageSize(size || 10);
                        await new Promise((r) => setTimeout(r, 50)); // 🕐 wait for state update
                        await refetch();
                    },
                }}
                rowKey={(record, index) => record.id ? record.id.toString() : index!.toString()}
                bordered
                scroll={{ x: "max-content" }}
                className="rounded-lg shadow-md"
            />
        </div>
    );



};

export default AuditLogPage;
